// Plane physics — shared between worker (authoritative) and client (predictor).
// Both call stepPlane(state, input, dt, dashActive). Identical math = identical
// trajectory given the same inputs, which makes client-side prediction viable.
//
// Body convention: forward = +X, up = +Y, right wing = +Z.
//   roll  → rotation around +X
//   pitch → rotation around +Z (+pitch lifts the nose)
//   yaw   → rotation around +Y

import { PLANE, TERRAIN } from "./protocol";

export type Quat = { x: number; y: number; z: number; w: number };

export interface PlaneState {
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  qx: number; qy: number; qz: number; qw: number;
  wpitch: number; wyaw: number; wroll: number;
  engineRpm: number; // 0..1, lags i.throttle by engineSpool seconds
}

export interface PlaneInput {
  pitch: number;    // -1..1
  yaw: number;
  roll: number;
  throttle: number; // 0..1
  autoLevel: boolean;
}

export const PHYS = {
  PITCH_RATE: 1.55,
  YAW_RATE:   1.00,
  ROLL_RATE:  3.4,
  ANG_RESP:   9.5,
  GRAVITY:    11.5,
  LIFT_K:     0.42,
  SIDESLIP:   1.6,
  // Bank-induced turn (real planes turn from bank). Higher = banking helps
  // the heading change harder, which is what the user means by "easier turn
  // when banked".
  BANK_TURN:  0.85,
  // Auto-bank reach: sin(bank) when cursor is at full deflection.
  // 0.78 ≈ 51°. Together with a stronger gain it makes cursor turns feel
  // immediate and WT-tight without overshoot.
  AUTO_BANK_TARGET: 0.78,
  AUTO_BANK_GAIN:   3.0,
};

export function clamp(v: number, lo: number, hi: number) { return v < lo ? lo : v > hi ? hi : v; }
export function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
export function len3(x: number, y: number, z: number) { return Math.hypot(x, y, z); }

export function qNormalize(q: Quat) {
  const n = Math.hypot(q.x, q.y, q.z, q.w) || 1;
  q.x /= n; q.y /= n; q.z /= n; q.w /= n;
}
export function qFromAxisAngle(ax: number, ay: number, az: number, angle: number): Quat {
  const half = angle * 0.5;
  const s = Math.sin(half);
  return { x: ax * s, y: ay * s, z: az * s, w: Math.cos(half) };
}
export function qMul(a: Quat, b: Quat): Quat {
  return {
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  };
}
export function qRotate(q: Quat, v: { x: number; y: number; z: number }) {
  const ix =  q.w * v.x + q.y * v.z - q.z * v.y;
  const iy =  q.w * v.y + q.z * v.x - q.x * v.z;
  const iz =  q.w * v.z + q.x * v.y - q.y * v.x;
  const iw = -q.x * v.x - q.y * v.y - q.z * v.z;
  return {
    x: ix * q.w + iw * -q.x + iy * -q.z - iz * -q.y,
    y: iy * q.w + iw * -q.y + iz * -q.x - ix * -q.z,
    z: iz * q.w + iw * -q.z + ix * -q.y - iy * -q.x,
  };
}

// Returns false if the plane crashed (hit floor) — caller decides what to do.
export function stepPlane(s: PlaneState, i: PlaneInput, dt: number, dashBoost: number, dashSpeedCapBonus: number): { crashed: boolean } {
  const speedNow = len3(s.vx, s.vy, s.vz);
  // Less variation than before: WT planes feel responsive at any speed.
  const speedFactor = clamp(speedNow / 60, 0.7, 1.1);

  // Current orientation vectors — needed for both auto-level AND target rates.
  const q: Quat = { x: s.qx, y: s.qy, z: s.qz, w: s.qw };
  const rightWorld = qRotate(q, { x: 0, y: 0, z: 1 });
  const fwdNow = qRotate(q, { x: 1, y: 0, z: 0 });

  // ===== Target angular rates =====
  // Sign convention reminder (right-handed coords, plane mesh = right wing
  // at body +Z, up at +Y, forward at +X):
  //   • +pitch (rot around +Z)  → forward → +Y      → nose UP
  //   • +roll  (rot around +X)  → +Y     → +Z      → right wing dips DOWN
  //   • +yaw   (rot around +Y)  → +X     → -Z      → forward swings LEFT
  // We want input.yaw +1 to mean "turn right", so we flip the yaw sign here
  // (single source of truth — caller never has to think about it).
  let targetWPitch = i.pitch * PHYS.PITCH_RATE * speedFactor;
  let targetWYaw   = -i.yaw  * PHYS.YAW_RATE   * speedFactor;
  let targetWRoll  = i.roll  * PHYS.ROLL_RATE;

  if (i.autoLevel && Math.abs(i.roll) < 0.05) {
    // Auto-bank into yaw direction (or level when not yawing).
    // Sign convention: positive wroll dips the RIGHT wing (rightWorld.y → -).
    // - cursor right → i.yaw > 0 → we want right-bank → rightWorld.y NEGATIVE
    //   → desiredBankSin negative → wroll positive (= -bankErr) drives down
    // - banked right and stable → rightWorld.y < 0, target 0 → wroll negative
    //   to roll back to level.
    const desiredBankSin = -i.yaw * PHYS.AUTO_BANK_TARGET;
    const bankErrSin = rightWorld.y - desiredBankSin;
    targetWRoll = bankErrSin * PHYS.AUTO_BANK_GAIN;
  }
  if (i.autoLevel && Math.abs(i.pitch) < 0.05) {
    // Maintain altitude: drive forward.y toward small positive (~ 0.05 nose up).
    const targetPitchSin = 0.05;
    const pitchErrSin = -fwdNow.y + targetPitchSin;
    // Blend with whatever pitch input gave (which is 0 here): full auto.
    targetWPitch = pitchErrSin * 1.4;
  }

  const angK = Math.min(1, PHYS.ANG_RESP * dt);
  s.wpitch = lerp(s.wpitch, targetWPitch, angK);
  s.wyaw   = lerp(s.wyaw,   targetWYaw,   angK);
  s.wroll  = lerp(s.wroll,  targetWRoll,  angK);

  // Banked turn coupling: when banked, lift bends the heading toward the
  // low wing. With our flipped yaw convention (negative wyaw = nose right),
  // banking right (rightWorld.y < 0) needs a NEGATIVE turn assist.
  const bankSin = rightWorld.y;
  const turnAssist = bankSin * PHYS.BANK_TURN;
  let wYawTotal = s.wyaw + turnAssist;

  const dqRoll  = qFromAxisAngle(1, 0, 0, s.wroll * dt);
  const dqPitch = qFromAxisAngle(0, 0, 1, s.wpitch * dt);
  const dqYaw   = qFromAxisAngle(0, 1, 0, wYawTotal * dt);
  const dq = qMul(qMul(dqYaw, dqPitch), dqRoll);
  const nq = qMul(q, dq);
  qNormalize(nq);
  s.qx = nq.x; s.qy = nq.y; s.qz = nq.z; s.qw = nq.w;

  const fwd = qRotate(nq, { x: 1, y: 0, z: 0 });
  const up  = qRotate(nq, { x: 0, y: 1, z: 0 });

  // ===== Engine RPM (real engines have spool inertia) =====
  // engineRpm chases the commanded throttle with a first-order time constant.
  const spoolK = Math.min(1, dt / PLANE.engineSpool);
  s.engineRpm = lerp(s.engineRpm, i.throttle, spoolK);

  // Thrust scales with engine RPM. Dash adds a flat bonus on top.
  const thrustMag = PLANE.baseThrust * s.engineRpm + dashBoost;
  s.vx += fwd.x * thrustMag * dt;
  s.vy += fwd.y * thrustMag * dt;
  s.vz += fwd.z * thrustMag * dt;

  // Gravity
  s.vy -= PHYS.GRAVITY * dt;

  // Lift (with stall — linear ramp from stallSpeed to liftReady)
  const fwdSpeed = Math.max(0, s.vx * fwd.x + s.vy * fwd.y + s.vz * fwd.z);
  const liftRamp = clamp((fwdSpeed - PLANE.stallSpeed) / (PLANE.liftReady - PLANE.stallSpeed), 0, 1);
  const lift = PLANE.liftCoef * fwdSpeed * PHYS.LIFT_K * liftRamp;
  s.vx += up.x * lift * dt;
  s.vy += up.y * lift * dt;
  s.vz += up.z * lift * dt;

  // Sideslip damping (perpendicular-to-forward velocity decays)
  const vDotF = s.vx * fwd.x + s.vy * fwd.y + s.vz * fwd.z;
  const vFx = fwd.x * vDotF, vFy = fwd.y * vDotF, vFz = fwd.z * vDotF;
  const vPx = s.vx - vFx, vPy = s.vy - vFy, vPz = s.vz - vFz;
  const sk = Math.max(0, 1 - PHYS.SIDESLIP * dt);
  s.vx = vFx + vPx * sk;
  s.vy = vFy + vPy * sk;
  s.vz = vFz + vPz * sk;

  // Drag (linear + quadratic, applied as deceleration opposite to velocity).
  const speed = Math.max(1e-6, len3(s.vx, s.vy, s.vz));
  const dragForce = PLANE.dragLinear * speed + PLANE.dragQuadratic * speed * speed;
  // Convert to scalar velocity reduction this dt (opposite to velocity vector).
  const dv = Math.min(speed, dragForce * dt);
  const dragFactor = (speed - dv) / speed;
  s.vx *= dragFactor; s.vy *= dragFactor; s.vz *= dragFactor;

  // Smooth speed cap (acts above max)
  const capSpeed = PLANE.maxSpeed + dashSpeedCapBonus;
  if (speed > capSpeed) {
    const over = speed - capSpeed;
    const reduce = Math.min(over, over * 4 * dt);
    const factor = (speed - reduce) / speed;
    s.vx *= factor; s.vy *= factor; s.vz *= factor;
  }

  // Integrate
  s.px += s.vx * dt;
  s.py += s.vy * dt;
  s.pz += s.vz * dt;

  // Soft boundary
  const half = TERRAIN.size / 2;
  const margin = 200;
  const pushBack = (axis: number, limit: number) => {
    if (axis > limit - margin) return -((axis - (limit - margin)) / margin) * 60;
    if (axis < -(limit - margin)) return (((-(limit - margin)) - axis) / margin) * 60;
    return 0;
  };
  s.vx += pushBack(s.px, half) * dt;
  s.vz += pushBack(s.pz, half) * dt;
  if (s.px > half)  { s.px = half;  s.vx = Math.min(0, s.vx); }
  if (s.px < -half) { s.px = -half; s.vx = Math.max(0, s.vx); }
  if (s.pz > half)  { s.pz = half;  s.vz = Math.min(0, s.vz); }
  if (s.pz < -half) { s.pz = -half; s.vz = Math.max(0, s.vz); }

  if (s.py > TERRAIN.worldBoundsY[1]) { s.py = TERRAIN.worldBoundsY[1]; s.vy = Math.min(0, s.vy); }
  if (s.py < TERRAIN.worldBoundsY[0]) return { crashed: true };
  return { crashed: false };
}
